/* global BX24 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

const isBitrixContext = () => typeof BX24 !== 'undefined' && BX24 !== null;

// В Битриксе iframe растягивается до высоты контента, а Битрикс скролит снаружи.
// position:fixed внутри iframe привязан к верху iframe, а не к экрану пользователя.
// Решение:
//   - window.visualViewport.pageTop — Y-смещение видимой области в координатах iframe-документа.
//     Обновляется при скроле родительского окна (Chrome/Firefox/Safari ≥ 13).
//   - Модал ставим position:absolute с top = pageTop + visibleHeight/2 → всегда по центру экрана.
//   - BX24.scrollParentWindow — резервный вызов на случай, если визуальный вьюпорт не обновился.

const getBitrixVisibleCenter = () => {
  const vv = window.visualViewport;
  if (vv) {
    return {
      centerY: vv.pageTop + vv.height / 2,
      centerX: vv.pageLeft + vv.width / 2,
      viewportH: vv.height,
    };
  }
  // Фолбэк: середина iframe-документа
  return {
    centerY: window.innerHeight / 2,
    centerX: window.innerWidth / 2,
    viewportH: window.innerHeight,
  };
};

const PositionedModal = ({
  show,
  onHide,
  children,
  dialogClassName = '',
  targetElement = null,
}) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && show) onHide();
    };
    if (show) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [show, onHide]);

  // Резервный скрол через BX24 API — на случай если visualViewport.pageTop
  // не обновился до того как отрисовался модал.
  useEffect(() => {
    if (!show || !isBitrixContext()) return;

    const timer = setTimeout(() => {
      if (!modalRef.current) return;

      const rect = modalRef.current.getBoundingClientRect();
      // getBoundingClientRect().top в Битриксе = абсолютная Y в iframe (скрол = 0 внутри).
      const modalAbsY = rect.top;
      const modalH = rect.height;

      // Если visualViewport уже дал правильные координаты — модал виден, скрол не нужен.
      const vv = window.visualViewport;
      if (vv && vv.pageTop > 0) {
        const vpTop = vv.pageTop;
        const vpBottom = vpTop + vv.height;
        const isVisible = modalAbsY + modalH > vpTop && modalAbsY < vpBottom;
        if (isVisible) return;
      }

      // Скролим Битрикс: центрируем модал по iframe-координатам.
      // BX24.scrollParentWindow принимает смещение в координатах родительской страницы.
      // IFRAME_TOP_OFFSET — приблизительное смещение iframe внутри страницы Битрикса.
      const IFRAME_TOP_OFFSET = 300;
      const PARENT_VIEWPORT_H = Math.min(window.screen.availHeight * 0.75, 750);
      const targetScroll = Math.max(
        0,
        IFRAME_TOP_OFFSET + modalAbsY + modalH / 2 - PARENT_VIEWPORT_H / 2
      );
      try {
        BX24.scrollParentWindow(targetScroll);
      } catch (_) {}
    }, 100);

    return () => clearTimeout(timer);
  }, [show]);

  const getModalPosition = () => {
    if (isBitrixContext()) {
      // В Битриксе: ставим по центру реально видимой области.
      const { centerY, centerX } = getBitrixVisibleCenter();
      return {
        top: `${centerY}px`,
        left: `${centerX}px`,
        transform: 'translate(-50%, -50%)',
        maxWidth: '600px',
        width: '500px',
        minWidth: '400px',
      };
    }

    if (targetElement) {
      const rect = targetElement.getBoundingClientRect();
      const margin = 12;
      const halfH = (window.innerHeight - 160) / 2;
      const halfW = 300;
      const rawX = rect.left + rect.width / 2;
      const rawY = rect.top + rect.height / 2;
      const centerX = Math.min(Math.max(rawX, halfW + margin), window.innerWidth - halfW - margin);
      const centerY = Math.min(Math.max(rawY, halfH + margin), window.innerHeight - halfH - margin);
      return {
        top: `${centerY}px`,
        left: `${centerX}px`,
        transform: 'translate(-50%, -50%)',
        maxWidth: '600px',
        width: '500px',
        minWidth: '400px',
      };
    }

    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      maxWidth: '600px',
      width: '500px',
      minWidth: '400px',
    };
  };

  if (!show) return null;

  const bitrix = isBitrixContext();
  const modalPosition = getModalPosition();

  // В Битриксе position:absolute — координаты относительно документа iframe.
  // В обычном браузере position:fixed — координаты относительно вьюпорта.
  const positionType = bitrix ? 'absolute' : 'fixed';

  const vv = bitrix && window.visualViewport;
  const viewportH = vv ? vv.height : window.innerHeight;

  const modalContent = (
    <>
      <div
        style={{
          position: positionType,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          minHeight: bitrix ? `${window.innerHeight}px` : undefined,
          backgroundColor: 'rgba(2, 2, 23, 0.55)',
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          zIndex: 9999,
        }}
        onClick={onHide}
      />

      <div
        ref={modalRef}
        className={`modal-dialog ${dialogClassName}`}
        style={{
          position: positionType,
          zIndex: 10000,
          margin: 0,
          maxHeight: `${viewportH - 160}px`,
          overflow: 'visible',
          ...modalPosition,
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (e.target.closest && e.target.closest('.btn-close')) {
            onHide();
          }
        }}
      >
        <div
          className="modal-content"
          style={{
            border: 'none',
            borderRadius: '18px',
            boxShadow: '0 30px 60px -20px rgba(2, 2, 23, 0.40), 0 10px 24px -10px rgba(2, 2, 23, 0.20)',
            maxHeight: `${viewportH - 160}px`,
            overflowY: 'auto',
            backgroundColor: '#ffffff',
          }}
        >
          <style>
            {`
              .modal-content { font-family: "Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif; color: #020217; }

              .modal-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 12px;
                border-bottom: 1px solid #eff3f7;
                padding: 20px 24px 16px;
                background: #ffffff;
                border-radius: 18px 18px 0 0;
              }

              .modal-title {
                font-family: "Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif;
                font-weight: 700;
                color: #020217;
                font-size: 20px;
                letter-spacing: -0.015em;
                line-height: 1.2;
              }

              .modal-body {
                padding: 18px 24px;
                color: #4a5875;
                font-family: "Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif;
                font-size: 15px;
                line-height: 1.6;
              }

              .modal-body strong, .modal-body b { color: #020217; font-weight: 700; }

              .modal-footer {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 10px;
                border-top: 1px solid #eff3f7;
                padding: 16px 24px 20px;
                background: #fafbfd;
                border-radius: 0 0 18px 18px;
              }

              .btn-close {
                flex: 0 0 auto;
                width: 30px;
                height: 30px;
                margin: 0;
                padding: 0;
                border: 1px solid #e6ecf3;
                border-radius: 8px;
                background-color: #f4f7fb;
                opacity: 1;
                transition: background-color 0.15s ease, border-color 0.15s ease;
              }

              .btn-close:hover {
                opacity: 1;
                background-color: #eaf2fb;
                border-color: #b6d3ee;
              }

              .btn {
                font-family: "Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif;
                border-radius: 12px;
                font-weight: 700;
                font-size: 15px;
                letter-spacing: -0.005em;
                padding: 11px 20px;
                transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
              }

              .btn:hover { transform: translateY(-1px); }

              .btn-primary {
                background: linear-gradient(135deg, #005ca9, #1f7fd1);
                border: 1px solid rgba(0, 92, 169, 0.35);
                color: #ffffff;
                box-shadow: 0 10px 26px rgba(0, 92, 169, 0.26), 0 2px 4px rgba(0, 92, 169, 0.14);
              }

              .btn-primary:hover {
                box-shadow: 0 14px 32px rgba(0, 92, 169, 0.32), 0 4px 8px rgba(0, 92, 169, 0.18);
              }

              .btn-success {
                background: linear-gradient(135deg, #0f766e, #15a394);
                border: 1px solid rgba(15, 118, 110, 0.35);
                color: #ffffff;
                box-shadow: 0 10px 26px rgba(15, 118, 110, 0.24);
              }

              .btn-danger {
                background: linear-gradient(135deg, #c0392b, #e05a4a);
                border: 1px solid rgba(192, 57, 43, 0.4);
                color: #ffffff;
                box-shadow: 0 10px 26px rgba(192, 57, 43, 0.22);
              }

              .btn-max {
                background: linear-gradient(135deg, #5e3ad6, #2a92e0);
                border: 1px solid rgba(94, 58, 214, 0.36);
                color: #ffffff;
                box-shadow: 0 10px 26px rgba(94, 58, 214, 0.22);
              }

              .btn-max-icon {
                display: inline-flex !important;
                align-items: center !important;
                justify-content: center !important;
                width: 44px !important;
                height: 44px !important;
                padding: 0 !important;
                border-radius: 14px !important;
                flex-shrink: 0;
              }

              .btn-max-icon img {
                filter: brightness(0) invert(1);
                width: 24px;
                height: 24px;
                object-fit: contain;
              }

              .modal-footer-split {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
              }

              .modal-footer-split .modal-footer-actions {
                display: flex;
                gap: 8px;
              }

              .btn-secondary {
                background: #ffffff;
                border: 1px solid #dbe3ef;
                color: #4a5875;
                box-shadow: 0 1px 2px rgba(2, 2, 23, 0.04);
              }

              .btn-secondary:hover {
                background: #f4f7fb;
                border-color: #b6d3ee;
                color: #005ca9;
              }

              .form-control, .form-select, textarea.form-control {
                border-radius: 10px;
                border: 1px solid #dbe3ef;
                font-family: "Inter", -apple-system, system-ui, "Segoe UI", Roboto, Arial, sans-serif;
                font-size: 15px;
                color: #020217;
                box-shadow: 0 1px 2px rgba(2, 2, 23, 0.05);
                transition: border-color 0.15s ease, box-shadow 0.15s ease;
              }

              .form-control:focus, .form-select:focus {
                border-color: #1f7fd1;
                box-shadow: 0 0 0 3px rgba(31, 127, 209, 0.18);
              }

              .alert {
                border-radius: 12px;
                border: 1px solid #dbe3ef;
                box-shadow: none;
              }
            `}
          </style>
          {children}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default PositionedModal;
